import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext, type DragEndEvent, KeyboardSensor, PointerSensor,
  closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Panel } from "../components/Panel";
import { Field, Select } from "../components/Field";
import { Button } from "../components/Button";
import { marketingPicksApi, type CurrentIssue, type PickRow, type SlotType } from "../api/marketing-picks";
import { FlipPreviewPanel } from "../marketing/FlipPreviewPanel";
import { ApiError } from "../api/client";
import { usePublicBaseUrl } from "../lib/publicBaseUrls";

const PUBS: Array<{ slug: string; name: string }> = [
  { slug: "flight_journal", name: "Flight Journal" },
  { slug: "model_airplane_news", name: "Model Airplane News" },
  { slug: "rc_car_action", name: "RC Car Action" },
  { slug: "diecast", name: "Die Cast X" },
];

const SLOT_LABELS: Record<SlotType, { label: string; max: number }> = {
  hero_trailer:    { label: "Hero trailer",    max: 1 },
  issue_spread:    { label: "Issue spreads",   max: 3 },
  sample_article:  { label: "Sample articles", max: 4 },
  issue_preview:   { label: "Flip-viewer preview", max: 1 },
};

const PUB_KEY = "marketing-landing.last-pub";

/** page-007.jpg — the filename convention the renderer writes into
    pages_base_url. Shared by both spread drawers. */
const paddedPage = (n: number) => String(n).padStart(3, "0");

/** URLs for the two facing pages of a spread starting at `startPage`.
    Returns nulls when the edition has no rendered pages to point at. */
function spreadPageUrls(
  baseUrl: string | null | undefined, startPage: number, query?: string | null,
) {
  if (!baseUrl) return { left: null, right: null };
  const base = baseUrl.replace(/\/$/, "");
  // The CDN refuses unsigned edition pages; the API signs a query for them.
  const q = query ? `?${query}` : "";
  return {
    left: `${base}/page-${paddedPage(startPage)}.jpg${q}`,
    right: `${base}/page-${paddedPage(startPage + 1)}.jpg${q}`,
  };
}

function IssueSpreadDrawer({
  open, onClose, pub, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  pub: string;
  onCreated: () => void;
}) {
  const [editionId, setEditionId] = useState<number | null>(null);
  const [startPage, setStartPage] = useState<number>(2);
  const [caption, setCaption] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["spread-editions", pub],
    queryFn: () => marketingPicksApi.candidates(pub, "issue_spread"),
    enabled: open,
  });

  const candidates = data?.candidates ?? [];
  const selected = candidates.find((c) => c.id === editionId) ?? null;

  // Live preview of the spread the public endpoint will render.
  const { left: previewLeftUrl, right: previewRightUrl } =
    spreadPageUrls(selected?.pages_base_url, startPage, selected?.pages_query);

  const onSubmit = async () => {
    if (!editionId) { setError("pick an edition"); return; }
    setBusy(true);
    setError(null);
    try {
      const trimmedLink = linkUrl.trim();
      const meta: Record<string, unknown> = { start_page: startPage };
      if (trimmedLink) meta.link_url = trimmedLink;
      await marketingPicksApi.create({
        publication_slug: pub,
        slot_type: "issue_spread",
        target_kind: "digital_edition",
        target_id: editionId,
        caption: caption.trim() || null,
        meta,
      });
      onCreated();
      onClose();
    } catch (e) {
      const detail = e instanceof ApiError && typeof e.detail === "object" && e.detail
        ? ((e.detail as { detail?: string }).detail ?? String(e))
        : String(e);
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const editionOptions = [
    { value: "", label: "Pick an issue…" },
    ...candidates.map((c) => ({
      value: String(c.id),
      label: `${c.title}${c.subtitle ? ` · ${c.subtitle}` : ""}${c.page_count ? ` · ${c.page_count} pp.` : ""}`,
    })),
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex justify-end" onClick={onClose}>
      <div
        className="w-[520px] bg-ink-900 border-l border-ink-500 p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-xl text-paper mb-4">Add issue spread</h2>

        <div className="mb-3">
          <Select
            label="Issue"
            value={editionId !== null ? String(editionId) : ""}
            onChange={(e) => setEditionId(Number(e.target.value) || null)}
            options={editionOptions}
          />
        </div>

        <Field
          label="Start page (left side of the spread)"
          type="number"
          min={1}
          max={selected?.page_count ? Math.max(1, selected.page_count - 1) : undefined}
          value={startPage}
          onChange={(e) => setStartPage(Number(e.target.value) || 1)}
        />
        <p className="label mt-1">renders as pp. {startPage}–{startPage + 1}</p>

        <label className="block mt-3 mb-3">
          <span className="label mb-1.5 block">Caption (optional, ≤240 chars)</span>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 240))}
            className="w-full bg-ink-700 border border-ink-500 px-2 py-1 text-paper rounded h-20"
          />
          <span className="label">{caption.length} / 240</span>
        </label>

        <Field
          label="Click-out URL (optional — e.g. advertiser site; opens in new tab)"
          type="url"
          placeholder="https://example.com"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value.slice(0, 500))}
        />

        {previewLeftUrl && previewRightUrl && (
          <div className="mb-3">
            <span className="label mb-1.5 block">Preview</span>
            <div className="flex gap-1 bg-ink-700 p-1 border border-ink-500 rounded">
              <img src={previewLeftUrl} alt={`page ${startPage}`}
                   className="w-1/2 aspect-[3/4] object-cover bg-ink-800" />
              <img src={previewRightUrl} alt={`page ${startPage + 1}`}
                   className="w-1/2 aspect-[3/4] object-cover bg-ink-800" />
            </div>
            <span className="label">pp. {startPage}–{startPage + 1}</span>
          </div>
        )}

        {error && <p className="text-red-400 mt-2">{error}</p>}

        <div className="mt-4 flex gap-2 justify-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !editionId} onClick={onSubmit}>Save</Button>
        </div>
      </div>
    </div>
  );
}

function ArticlePickDrawer({
  open, onClose, pub, slotType, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  pub: string;
  slotType: "hero_trailer" | "sample_article";
  onCreated: () => void;
}) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["pick-candidates", pub, slotType, q],
    queryFn: () => marketingPicksApi.candidates(pub, slotType, q.length >= 2 ? q : undefined),
    enabled: open,
  });

  const onPick = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      await marketingPicksApi.create({
        publication_slug: pub,
        slot_type: slotType,
        target_kind: "archive_article",
        target_id: id,
      });
      onCreated();
      onClose();
    } catch (e) {
      const detail = e instanceof ApiError && typeof e.detail === "object" && e.detail
        ? ((e.detail as { detail?: string }).detail ?? String(e))
        : String(e);
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex justify-end" onClick={onClose}>
      <div
        className="w-[480px] bg-ink-900 border-l border-ink-500 p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-xl text-paper mb-4">
          Add {slotType === "hero_trailer" ? "hero trailer" : "sample article"}
        </h2>
        <Field
          label="Search by title or slug"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type to search…"
        />
        {error && <p className="text-red-400 mt-2">{error}</p>}
        <ul className="mt-4 divide-y divide-ink-500">
          {(data?.candidates ?? []).map((c) => (
            <li key={c.id} className="py-2 flex gap-3 items-center">
              {c.image_url && <img src={c.image_url} alt="" className="w-14 h-10 object-cover rounded-sm" />}
              <div className="flex-1 min-w-0">
                <div className="font-serif text-paper text-sm truncate">{c.title}</div>
                <div className="label">{c.subtitle}</div>
              </div>
              <Button disabled={busy} onClick={() => onPick(c.id)}>Add</Button>
            </li>
          ))}
          {(data?.candidates ?? []).length === 0 && (
            <li className="py-3 label">
              No matches. Trailer-required slots only show articles with a published trailer —
              {' '}<a className="text-amber-glow underline" href="/lab/trailers">publish a trailer first</a>.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function EditPickDrawer({
  pick, onClose, onSaved,
}: {
  pick: PickRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [caption, setCaption] = useState(pick.caption ?? "");
  const [excerpt, setExcerpt] = useState(pick.excerpt_override ?? "");
  const initialMeta = (pick.meta ?? {}) as { start_page?: number; link_url?: string };
  const [linkUrl, setLinkUrl] = useState(initialMeta.link_url ?? "");
  const [startPage, setStartPage] = useState<number>(
    typeof initialMeta.start_page === "number" ? initialMeta.start_page : 1,
  );
  const [editionId, setEditionId] = useState<number>(pick.target_id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSpread = pick.slot_type === "issue_spread";

  // Issues this spread can be repointed at. The endpoint returns the 20
  // most recent editions, so the pick's own edition may not be among them
  // — it's prepended below from the row's own target preview so the select
  // never silently drops what's currently selected.
  const { data: editionData } = useQuery({
    queryKey: ["spread-editions", pick.publication_slug],
    queryFn: () =>
      marketingPicksApi.candidates(pick.publication_slug, "issue_spread"),
    enabled: isSpread,
  });

  const editions = useMemo(() => {
    const list = editionData?.candidates ?? [];
    if (list.some((c) => c.id === pick.target_id) || !pick.target) return list;
    return [
      {
        kind: "digital_edition" as const,
        id: pick.target_id,
        title: pick.target.title,
        subtitle: pick.target.subtitle,
        image_url: pick.target.image_url,
        trailer_published: null,
        page_count: pick.target.page_count,
        pages_base_url: pick.target.pages_base_url,
        pages_query: pick.target.pages_query,
      },
      ...list,
    ];
  }, [editionData, pick.target_id, pick.target]);

  const selectedEdition = editions.find((c) => c.id === editionId) ?? null;

  // Which pages the spread will show once saved. pages_base_url comes off
  // the selected edition, so the preview follows both controls live.
  const maxStartPage = selectedEdition?.page_count
    ? Math.max(1, selectedEdition.page_count - 1)
    : undefined;
  const { left: previewLeftUrl, right: previewRightUrl } =
    spreadPageUrls(selectedEdition?.pages_base_url, startPage, selectedEdition?.pages_query);

  // Switching to a shorter issue would otherwise submit a start_page that
  // issue doesn't have and bounce off the server's validation.
  const onPickEdition = (id: number) => {
    setEditionId(id);
    const next = editions.find((c) => c.id === id);
    if (next?.page_count) {
      setStartPage((sp) => Math.min(sp, Math.max(1, next.page_count! - 1)));
    }
  };

  const onSave = async () => {
    setBusy(true);
    setError(null);
    try {
      // For issue_spread we re-send the full meta so the server's
      // wholesale-replacement of `meta` keeps start_page intact.
      const patch: {
        caption: string;
        excerpt_override: string;
        meta?: Record<string, unknown>;
        target_id?: number;
      } = { caption, excerpt_override: excerpt };
      if (isSpread && editionId !== pick.target_id) {
        patch.target_id = editionId;
      }
      if (pick.slot_type === "issue_spread") {
        const meta: Record<string, unknown> = { ...initialMeta };
        meta.start_page = startPage;
        const trimmedLink = linkUrl.trim();
        if (trimmedLink) meta.link_url = trimmedLink;
        else delete meta.link_url;
        patch.meta = meta;
      }
      await marketingPicksApi.patch(pick.id, patch);
      onSaved();
      onClose();
    } catch (e) {
      const detail = e instanceof ApiError && typeof e.detail === "object" && e.detail
        ? ((e.detail as { detail?: string }).detail ?? String(e))
        : String(e);
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex justify-end" onClick={onClose}>
      <div
        className="w-[520px] bg-ink-900 border-l border-ink-500 p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-xl text-paper mb-4">Edit pick</h2>
        <p className="label mb-4">
          {pick.target?.title ?? `target #${pick.target_id}`} · {pick.slot_type.replace(/_/g, " ")}
          {pick.slot_type === "issue_spread"
            && typeof initialMeta.start_page === "number"
            && <> · currently pp. {initialMeta.start_page}–{initialMeta.start_page + 1}</>}
        </p>

        {pick.slot_type === "issue_spread" && (
          <>
            <div className="mb-3">
              <Select
                label="Issue"
                value={String(editionId)}
                onChange={(e) => onPickEdition(Number(e.target.value))}
                options={editions.map((c) => ({
                  value: String(c.id),
                  label: `${c.title}${c.subtitle ? ` · ${c.subtitle}` : ""}${
                    c.page_count ? ` · ${c.page_count} pp.` : ""
                  }`,
                }))}
              />
            </div>

            <Field
              label="Start page (left side of the spread)"
              type="number"
              min={1}
              max={maxStartPage}
              value={startPage}
              onChange={(e) => setStartPage(Number(e.target.value) || 1)}
            />
            <p className="label mt-1 mb-3">
              renders as pp. {startPage}–{startPage + 1}
              {pick.target?.page_count ? ` of ${pick.target.page_count}` : ""}
            </p>

            {previewLeftUrl && previewRightUrl && (
              <div className="mb-3">
                <span className="label mb-1.5 block">Preview</span>
                <div className="flex gap-1 bg-ink-700 p-1 border border-ink-500 rounded">
                  <img src={previewLeftUrl} alt={`page ${startPage}`}
                       className="w-1/2 aspect-[3/4] object-cover bg-ink-800" />
                  <img src={previewRightUrl} alt={`page ${startPage + 1}`}
                       className="w-1/2 aspect-[3/4] object-cover bg-ink-800" />
                </div>
              </div>
            )}

            <label className="block mb-3">
              <span className="label mb-1.5 block">Caption (≤240 chars)</span>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 240))}
                className="w-full bg-ink-700 border border-ink-500 px-2 py-1 text-paper rounded h-20"
              />
              <span className="label">{caption.length} / 240</span>
            </label>
            <Field
              label="Click-out URL (optional — e.g. advertiser site; opens in new tab)"
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value.slice(0, 500))}
            />
          </>
        )}

        {pick.slot_type === "sample_article" && (
          <label className="block mb-3">
            <span className="label mb-1.5 block">Excerpt override (≤600 chars; blank = auto-pull first 150 words)</span>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value.slice(0, 600))}
              className="w-full bg-ink-700 border border-ink-500 px-2 py-1 text-paper rounded h-32"
              placeholder="Leave blank to use the auto-extracted excerpt."
            />
            <div className="flex justify-between">
              <span className="label">{excerpt.length} / 600</span>
              <button
                type="button"
                className="label text-amber-glow"
                onClick={() => setExcerpt("")}
              >Clear (use auto)</button>
            </div>
          </label>
        )}

        {error && <p className="text-red-400 mt-2">{error}</p>}
        <div className="mt-4 flex gap-2 justify-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={onSave}>Save</Button>
        </div>
      </div>
    </div>
  );
}

function SortablePickRow({ pick, onDelete, onEdit, currentIssue }: {
  pick: PickRow;
  onDelete: () => void;
  onEdit: () => void;
  /** Issue-spread rows: the issue the homepage follows. A pick for any
      other issue is ignored there. */
  currentIssue?: CurrentIssue | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: pick.id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const meta = (pick.meta as { start_page?: number; link_url?: string } | null) ?? {};
  const startPage = meta.start_page;
  const linkUrl = meta.link_url;
  return (
    <li ref={setNodeRef} style={style} className="py-3 flex gap-3 items-start">
      <button
        type="button"
        className="px-2 cursor-grab text-paper-trace hover:text-amber-glow"
        aria-label="Reorder"
        {...attributes}
        {...listeners}
      >≡</button>
      {pick.target?.image_url && (
        <img src={pick.target.image_url} alt="" className="w-20 h-12 object-cover rounded-sm bg-ink-700" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-serif text-paper text-sm truncate">
          {pick.target?.title ?? `(target #${pick.target_id})`}
        </div>
        <div className="label">
          {pick.target?.subtitle ?? pick.target_kind}
          {pick.slot_type === "issue_spread" && startPage && (
            <> · pp. {startPage}–{startPage + 1}</>
          )}
          {pick.slot_type === "issue_spread" && linkUrl && (
            <span className="text-amber-glow ml-2" title={linkUrl}>
              ↗ click-out
            </span>
          )}
          {pick.slot_type === "issue_spread" && currentIssue && (
            pick.target_id === currentIssue.id
              ? <span className="text-emerald-400 ml-2">● on the homepage</span>
              : <span className="text-red-400 ml-2" title={`The homepage follows ${currentIssue.title}`}>
                  ⚠ not shown — not the current issue
                </span>
          )}
          {pick.target?.trailer_published === false && (
            <span className="text-red-400 ml-2">⚠ trailer not published</span>
          )}
        </div>
        {pick.caption && (
          <p className="text-paper-dim text-sm mt-1 italic">"{pick.caption}"</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={onEdit}>Edit</Button>
        <Button variant="danger" onClick={onDelete}>Remove</Button>
      </div>
    </li>
  );
}

export function MarketingLandingPage() {
  const [pub, setPub] = useState<string>(() => {
    return localStorage.getItem(PUB_KEY) || "flight_journal";
  });
  useEffect(() => { localStorage.setItem(PUB_KEY, pub); }, [pub]);
  const previewBase = usePublicBaseUrl(pub);

  const [addingSlot, setAddingSlot] = useState<SlotType | null>(null);
  const [editingPickId, setEditingPickId] = useState<number | null>(null);

  const qc = useQueryClient();
  const queryKey = ["marketing-picks", pub] as const;

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => marketingPicksApi.list(pub),
    staleTime: 30_000,
  });

  // Single helper used by every mutation here so the cache invalidation
  // can't drift. invalidateQueries triggers a refetch AND marks the
  // existing data stale so any consumer re-renders with fresh state.
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["marketing-picks", pub] });

  const grouped = useMemo(() => {
    const g: Record<SlotType, PickRow[]> = {
      hero_trailer: [], issue_spread: [], sample_article: [], issue_preview: [],
    };
    (data?.picks ?? []).forEach((p) => {
      g[p.slot_type].push(p);
    });
    return g;
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Mutations — useMutation wraps the API calls so onSuccess can
  // invalidate the cache reliably (vs. the old refetch() which didn't
  // mark stale and could keep the old data on screen until refresh).
  const deleteMutation = useMutation({
    mutationFn: (pickId: number) => marketingPicksApi.delete(pickId),
    onSuccess: refresh,
  });

  const onDragEnd = async (slot: SlotType, evt: DragEndEvent) => {
    if (!evt.over || evt.active.id === evt.over.id) return;
    const items = grouped[slot];
    const oldIdx = items.findIndex((p) => p.id === Number(evt.active.id));
    const newIdx = items.findIndex((p) => p.id === Number(evt.over!.id));
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(items, oldIdx, newIdx).map((p) => p.id);
    await marketingPicksApi.reorder(pub, slot, reordered);
    await refresh();
  };

  return (
    <div className="px-8 py-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-paper">Marketing landing</h1>
          <p className="label mt-1">
            Curated picks for the title's signed-out landing page
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            label="Publication"
            options={PUBS.map((p) => ({ value: p.slug, label: p.name }))}
            value={pub}
            onChange={(e) => setPub(e.target.value)}
          />
          {previewBase && (
            <a
              href={`${previewBase}/`}
              target="_blank" rel="noreferrer"
              className="text-amber-glow font-mono text-xs underline"
            >
              View landing page →
            </a>
          )}
        </div>
      </header>

      {isLoading && <p className="label">Loading…</p>}
      {error instanceof Error && <p className="text-red-400">{error.message}</p>}

      {(["hero_trailer", "issue_spread", "sample_article"] as SlotType[]).map((slot) => {
        const items = grouped[slot];
        const meta = SLOT_LABELS[slot];
        return (
          <Panel
            key={slot}
            label={`${meta.label} · ${items.length} of ${meta.max} max`}
          >
            {items.length === 0 && (
              <p className="label">No {meta.label.toLowerCase()} picked yet — nothing to render here on the landing page (the public endpoint will use auto-pulled fallbacks).</p>
            )}
            {slot === "issue_spread" && (
              <p className="label mb-2">
                {data?.current_issue
                  ? <>The homepage follows <span className="text-paper">{data.current_issue.title}</span>. </>
                  : null}
                Picks for the current issue replace the automatic spreads (the
                opening pages of its first three features). Picks for any other
                issue are ignored, so when a new issue is published the section
                goes back to automatic until you pick spreads from it.
              </p>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                        onDragEnd={(e) => onDragEnd(slot, e)}>
              <SortableContext items={items.map((p) => p.id)}
                               strategy={verticalListSortingStrategy}>
                <ul className="divide-y divide-ink-500">
                  {items.map((p) => (
                    <SortablePickRow
                      key={p.id}
                      pick={p}
                      onDelete={() => deleteMutation.mutate(p.id)}
                      onEdit={() => setEditingPickId(p.id)}
                      currentIssue={data?.current_issue}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
            <div className="mt-3">
              <Button onClick={() => setAddingSlot(slot)}>+ Add {meta.label.toLowerCase().replace(/s$/, "")}</Button>
            </div>
          </Panel>
        );
      })}

      <FlipPreviewPanel
        pub={pub}
        currentIssue={data?.current_issue ?? null}
        picks={grouped.issue_preview}
        onChanged={refresh}
      />

      {(addingSlot === "hero_trailer" || addingSlot === "sample_article") ? (
        <ArticlePickDrawer
          open
          onClose={() => setAddingSlot(null)}
          pub={pub}
          slotType={addingSlot}
          onCreated={refresh}
        />
      ) : null}

      {addingSlot === "issue_spread" ? (
        <IssueSpreadDrawer
          open
          onClose={() => setAddingSlot(null)}
          pub={pub}
          onCreated={refresh}
        />
      ) : null}

      {editingPickId !== null && (() => {
        const pick = (data?.picks ?? []).find((p) => p.id === editingPickId);
        if (!pick) return null;
        return (
          <EditPickDrawer
            pick={pick}
            onClose={() => setEditingPickId(null)}
            onSaved={refresh}
          />
        );
      })()}
    </div>
  );
}
